
import { useState, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCacheCoordinator } from "@/hooks/useCacheCoordinator";

interface UseExerciseOperationsProps {
  routineId: string;
  localExercises: any[];
  setLocalExercises: (exercises: any[]) => void;
  onExerciseChange: () => void;
  updateExerciseField: (routineId: string, exerciseId: string, field: string, value: any) => void;
}

export const useExerciseOperations = ({
  routineId,
  localExercises,
  setLocalExercises,
  onExerciseChange,
  updateExerciseField
}: UseExerciseOperationsProps) => {
  const [selectedExercise, setSelectedExercise] = useState<any>(null);
  const [editExerciseName, setEditExerciseName] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  const { lockOperation, unlockOperation, updateExerciseCache, invalidateExercises } = useCacheCoordinator({ routineId });

  const handleUpdateExercise = useCallback(async (exerciseId: string, field: string, value: number | any[]) => {
    const operationId = `update-${exerciseId}-${field}-${Date.now()}`;
    
    try {
      lockOperation(operationId);
      
      const updateData: any = {};
      
      if (field === 'set_data') {
        updateData.set_data = value;
      } else {
        updateData[field] = value;
      }

      console.log(`🎯 Coordinated exercise update: ${exerciseId}.${field}`, value);

      // Apply optimistic update
      const updatedExercises = localExercises.map(ex => 
        ex.id === exerciseId ? { ...ex, [field]: value } : ex
      );
      setLocalExercises(updatedExercises);
      updateExerciseCache(updatedExercises);

      // Update database
      const { error } = await supabase
        .from('exercises')
        .update(updateData)
        .eq('id', exerciseId);

      if (error) throw error;

      console.log(`✅ Exercise update completed: ${exerciseId}.${field}`);
      
      // Schedule cache refresh
      setTimeout(() => {
        unlockOperation(operationId);
        invalidateExercises(100);
      }, 300);
      
    } catch (error) {
      console.error('Error updating exercise:', error);
      unlockOperation(operationId);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo actualizar el ejercicio",
      });
    }
  }, [localExercises, setLocalExercises, routineId, lockOperation, unlockOperation, updateExerciseCache, invalidateExercises]);

  const handleDeleteExercise = useCallback(async (exerciseId: string) => {
    const operationId = `delete-${exerciseId}-${Date.now()}`;
    
    try {
      lockOperation(operationId);
      
      const { error } = await supabase
        .from('exercises')
        .delete()
        .eq('id', exerciseId);

      if (error) throw error;

      toast({
        title: "Ejercicio eliminado",
        description: "El ejercicio ha sido eliminado exitosamente",
      });

      // Update local state
      const updatedExercises = localExercises.filter(ex => ex.id !== exerciseId);
      setLocalExercises(updatedExercises);
      updateExerciseCache(updatedExercises);
      
      setTimeout(() => {
        unlockOperation(operationId);
        onExerciseChange();
      }, 300);
      
    } catch (error) {
      console.error('Error deleting exercise:', error);
      unlockOperation(operationId);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar el ejercicio",
      });
    }
  }, [localExercises, setLocalExercises, onExerciseChange, routineId, lockOperation, unlockOperation, updateExerciseCache]);

  const handleEditClick = useCallback((exercise: any) => {
    setSelectedExercise(exercise.id);
    setEditExerciseName(exercise.name);
    setIsEditDialogOpen(true);
  }, []);

  return {
    selectedExercise,
    editExerciseName,
    isEditDialogOpen,
    setEditExerciseName,
    setIsEditDialogOpen,
    handleUpdateExercise,
    handleDeleteExercise,
    handleEditClick
  };
};
